/**
 * components/dashboard/StatusBadge.tsx
 * ──────────────────────────────────────
 * Color-coded pill badge rendering a ReconciliationFlag state.
 *
 *  MATCH             → green
 *  DISCREPANCY_COUNT → amber
 *  MISMATCH_SKU      → red
 *  EMPTY_SLOT        → slate
 *  DETECTION_FAILURE → purple
 */

import type { ReconciliationFlag } from '@/types';

const FLAG_LABELS: Record<ReconciliationFlag, string> = {
  MATCH:             '✓ Match',
  DISCREPANCY_COUNT: '⚠ Mismatch',
  MISMATCH_SKU:      '✕ SKU Mismatch',
  EMPTY_SLOT:        '○ Empty',
  DETECTION_FAILURE: '◐ N/A',
};

const FLAG_CLASSES: Record<ReconciliationFlag, string> = {
  MATCH:             'badge badge--match',
  DISCREPANCY_COUNT: 'badge badge--discrepancy',
  MISMATCH_SKU:      'badge badge--mismatch',
  EMPTY_SLOT:        'badge badge--empty',
  DETECTION_FAILURE: 'badge badge--failure',
};

interface StatusBadgeProps {
  flag: ReconciliationFlag;
  size?: 'sm' | 'md';
}

export function StatusBadge({ flag, size = 'md' }: StatusBadgeProps) {
  return (
    <span className={`${FLAG_CLASSES[flag]} badge--${size}`}>
      {FLAG_LABELS[flag]}
    </span>
  );
}

/** Return just the CSS class string without rendering a component — useful
 *  when you need to style a parent element based on the flag. */
export function flagClass(flag: ReconciliationFlag): string {
  return FLAG_CLASSES[flag];
}

/** True when the flag represents any non-matching state. */
export function isDiscrepancy(flag: ReconciliationFlag): boolean {
  return flag !== 'MATCH';
}
